import * as React from 'react';
import { useRef, useState, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  Snackbar,
  Tooltip,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useUserStore } from '../../store';
import { can } from '../../utils/access';
import BookingNotifyChoice from '../shared/BookingNotifyChoice';
import useCalendarState from './hooks/useCalendarState';
import { HOUR_HEIGHT, SNAP_PIXELS, SNAP_MINUTES, SNAPS_PER_HOUR } from './calendarConstants';
import { parseISO, addMinutes, format, parseWorkScheduleHours } from './hooks/dateUtils';
import { detectConflicts } from './hooks/conflictUtils';

// Drag detection thresholds
const DRAG_DELAY_MS = 150; // Hold for this long before drag starts
const DRAG_MOVE_THRESHOLD = 5; // Or move this many pixels to start drag immediately

function snapToGrid(pixels) {
  return Math.round(pixels / SNAP_PIXELS) * SNAP_PIXELS;
}

/**
 * Wrapper component that makes calendar events draggable and resizable.
 * Uses direct DOM manipulation during drag for smooth performance (no React re-renders).
 * Only updates React state on mouseUp.
 */
function DraggableEvent({
  children,
  event,
  eventType, // 'maintenance' | 'unavailability' | 'calendar_note'
  top,
  height,
  backgroundColor,
  borderColor,
  ringColor = null,        // colour tag drawn as a ring ('border' mode); null for 'fill'
  colorTagId = null,
  colorTagMode = null,
  highlighted = false,     // a search or "View in calendar" link points at this card
  onClick,
  sx = {},
  dayIndex, // Which day column this instance is rendered in (for multi-day events)
  overlapIndex = 0, // Horizontal position index for overlapping events
  overlapTotal = 1, // Total number of overlapping events
  tooltipContent, // React node for hover tooltip
  resourceId, // Which resource column this instance is in (employee or bay ID)
  // The grid's whole-hour rows. A day view's rows are that day's hours, not the week's, so drag
  // and resize must map pixels to time with the rows actually drawn.
  gridStartHour,
  gridEndHour,
}) {
  const canSchedule = useUserStore((s) => can(s.currentOrganization, 'work_orders.schedule'));
  // Boolean, not the object: every card mounts this and it only flips on open/close
  const isAnyEventMenuOpen = useCalendarState((s) => s.eventContextMenu !== null);
  const {
    eventDragState,
    startEventDrag,
    endEventDrag,
    updateEvent,
    openEventContextMenu,
    updateUnavailability,
    updateCalendarNote,
    workSchedule,
    events, // All scheduled events for conflict detection
    unavailabilities, // Employee unavailabilities for conflict detection
    employees,
    serviceBays,
    currentView,
    getEffectiveWorkHours,
  } = useCalendarState();

  const { t } = useTranslation();
  const eventRef = useRef(null);
  const wasDraggingRef = useRef(false);
  const pendingDragRef = useRef(null); // Track pending drag before threshold
  const dragTravelledRef = useRef(false); // Pointer went past DRAG_MOVE_THRESHOLD during this drag
  const dragDelayTimerRef = useRef(null);
  const localDragOffsetRef = useRef({ top: 0, height: 0, left: 0 }); // Ref for reading in handlers
  const targetDayInfoRef = useRef(null); // Ref for cross-day info (NO STATE - refs only!)
  const crossDayOverlayRef = useRef(null); // Fixed overlay for cross-day preview
  const timeLabelRef = useRef(null); // Floating time label during drag/resize
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false); // controlled Tooltip, see render
  const [confirmDialog, setConfirmDialog] = useState({ open: false, pendingDelta: 0, newTimes: null, sourceResourceId: null, targetResourceId: null });
  const [updateError, setUpdateError] = useState(null);
  // null | 'notified' | 'silent' — drives the "linked booking updated" snackbar text
  const [bookingSynced, setBookingSynced] = useState(null);
  // Per-move choice for booking-linked work orders; reset to on every time the dialog closes
  const [notifyCustomer, setNotifyCustomer] = useState(true);

  // Check if THIS specific instance is being dragged (not other day/resource instances of same event)
  const isBeingDragged = eventDragState?.event?.id === event.id &&
    eventDragState?.eventType === eventType &&
    (dayIndex === undefined || eventDragState?.originalColumn?.dayIndex === dayIndex) &&
    (resourceId === undefined || eventDragState?.originalColumn?.resourceId === resourceId);
  const dragType = isBeingDragged ? eventDragState.type : null;

  // Check if ANY event drag is happening (for disabling transitions globally)
  const isAnyDragActive = eventDragState !== null;

  // Work hours for clamping: the grid's rows (whole hours, as drawn)
  const effectiveHours = parseWorkScheduleHours(getEffectiveWorkHours());
  const startHour = gridStartHour ?? Math.floor(effectiveHours.startHour);
  const endHour = gridEndHour ?? Math.ceil(effectiveHours.endHour);
  const workDayPixels = (endHour - startHour) * HOUR_HEIGHT;
  // Resize handles are 8 px, but a quarter of the card at most (3 px floor), so a 15 min card
  // keeps a middle that moves the card instead of being all handle
  const resizeHandleHeight = Math.max(3, Math.min(8, Math.floor(height / 4)));

  // Conflict detection for drag confirmation dialog
  const dragConflicts = useMemo(() => {
    if (!confirmDialog.open || !confirmDialog.newTimes || eventType !== 'maintenance') {
      return [];
    }

    // Calculate employee_ids considering pending swap
    let effectiveEmployeeIds = event.employee_ids || [];
    if (confirmDialog.sourceResourceId && confirmDialog.targetResourceId &&
        confirmDialog.sourceResourceId !== confirmDialog.targetResourceId &&
        currentView === 'employee') {
      effectiveEmployeeIds = effectiveEmployeeIds
        .filter(id => id !== confirmDialog.sourceResourceId);
      if (!effectiveEmployeeIds.includes(confirmDialog.targetResourceId)) {
        effectiveEmployeeIds = [...effectiveEmployeeIds, confirmDialog.targetResourceId];
      }
    }

    return detectConflicts({
      plannedStart: confirmDialog.newTimes.newStart,
      plannedEnd: confirmDialog.newTimes.newEnd,
      employeeIds: effectiveEmployeeIds,
      serviceBayId: event.service_bay_id,
      existingEvents: events,
      unavailabilities,
      excludeEventId: event.id,
      employees,
      serviceBays,
      t,
    });
  }, [confirmDialog.open, confirmDialog.newTimes, confirmDialog.sourceResourceId, confirmDialog.targetResourceId, currentView, eventType, event, events, unavailabilities, employees, serviceBays, t]);

  // Find which day column the mouse is over
  // Uses both X and Y position to handle multiple employee/resource rows
  const findTargetColumn = useCallback((clientX, clientY) => {
    const columns = document.querySelectorAll('[data-day-column]');
    for (const col of columns) {
      const rect = col.getBoundingClientRect();
      const xMatch = clientX >= rect.left && clientX <= rect.right;
      // If clientY provided, also check Y bounds; otherwise just match X (for backwards compat)
      const yMatch = clientY === undefined || (clientY >= rect.top && clientY <= rect.bottom);
      if (xMatch && yMatch) {
        return {
          dayIndex: parseInt(col.dataset.dayColumn, 10),
          date: col.dataset.dayDate,
          resourceId: col.dataset.resourceId ? parseInt(col.dataset.resourceId, 10) : null,
          columnLeft: rect.left,
          columnWidth: rect.width,
          columnTop: rect.top,
          columnBottom: rect.bottom,
        };
      }
    }
    return null;
  }, []);

  // Update cross-day overlay position (direct DOM, no React state)
  // type: 'extend' (resize-bottom forward), 'shrink', 'extend-top' (resize-top backward)
  const updateCrossDayOverlay = useCallback((targetColumn, clampedHour, type) => {
    const overlay = crossDayOverlayRef.current;
    if (!overlay || !targetColumn) {
      if (overlay) overlay.style.display = 'none';
      return;
    }

    let overlayTop, overlayHeight;

    if (type === 'extend-top') {
      // For resize-top extending to earlier day: show from clampedHour to end of day
      overlayTop = (clampedHour - startHour) * HOUR_HEIGHT;
      overlayHeight = (endHour - clampedHour) * HOUR_HEIGHT;
    } else if (type === 'shrink-top') {
      // For resize-top shrinking to later day: show from new start hour to end of day
      overlayTop = (clampedHour - startHour) * HOUR_HEIGHT;
      overlayHeight = (endHour - clampedHour) * HOUR_HEIGHT;
    } else {
      // For resize-bottom (extend/shrink): show from start of day to clampedHour
      overlayTop = 0;
      overlayHeight = (clampedHour - startHour) * HOUR_HEIGHT;
    }

    overlay.style.display = 'block';
    overlay.style.position = 'fixed';
    overlay.style.top = `${targetColumn.columnTop + overlayTop}px`;
    overlay.style.left = `${targetColumn.columnLeft + 4}px`;
    overlay.style.width = `${targetColumn.columnWidth - 8}px`;
    overlay.style.height = `${Math.max(SNAP_PIXELS, overlayHeight)}px`;
    overlay.style.backgroundColor = 'rgba(156, 163, 175, 0.3)';
    overlay.style.border = '2px dashed #9ca3af';
    overlay.style.borderRadius = '8px';
    overlay.style.zIndex = '1000';
    overlay.style.pointerEvents = 'none';
  }, [startHour, endHour]);

  // Hide cross-day overlay
  const hideCrossDayOverlay = useCallback(() => {
    const overlay = crossDayOverlayRef.current;
    if (overlay) {
      overlay.style.display = 'none';
    }
  }, []);

  // Format hour decimal to HH:MM string
  const formatHourToTime = (date) => {
    const h = date.getHours().toString().padStart(2, '0');
    const m = date.getMinutes().toString().padStart(2, '0');
    return `${h}:${m}`;
  };

  // Snap a Date's minutes to the nearest SNAP_MINUTES boundary
  const snapTime = (date) => {
    const result = new Date(date);
    const m = result.getMinutes();
    result.setMinutes(Math.round(m / SNAP_MINUTES) * SNAP_MINUTES, 0, 0);
    return result;
  };

  // Calculate new times from drag delta, optionally with a new target date
  // (defined before updateTimeLabel which depends on it)
  const calculateNewTimes = useCallback((snappedDeltaY, currentDragType, currentEventDragState, newTargetDate = null) => {
    if (!currentEventDragState) return null;

    const deltaMinutes = (snappedDeltaY / HOUR_HEIGHT) * 60;
    const originalStart = parseISO(currentEventDragState.originalEvent.start_time);
    const originalEnd = parseISO(currentEventDragState.originalEvent.end_time);

    let newStart, newEnd;

    if (currentDragType === 'move') {
      const originalDurationMs = originalEnd.getTime() - originalStart.getTime();
      newStart = snapTime(addMinutes(originalStart, deltaMinutes));
      if (newTargetDate) {
        const [year, month, day] = newTargetDate.split('-').map(Number);
        const startHours = newStart.getHours();
        const startMins = newStart.getMinutes();
        newStart = new Date(year, month - 1, day, startHours, startMins, 0);
      }
      newEnd = new Date(newStart.getTime() + originalDurationMs);
    } else if (currentDragType === 'resize-top') {
      const targetDate = typeof newTargetDate === 'object' ? newTargetDate?.date : null;
      const targetStartHour = typeof newTargetDate === 'object' ? newTargetDate?.startHour : null;
      const isResizeTop = typeof newTargetDate === 'object' ? newTargetDate?.isResizeTop : false;

      if (isResizeTop && targetDate && targetStartHour !== null) {
        const [year, month, day] = targetDate.split('-').map(Number);
        const hourPart = Math.floor(targetStartHour);
        const minutePart = Math.round((targetStartHour - hourPart) * 60);
        newStart = new Date(year, month - 1, day, hourPart, minutePart, 0);
        newEnd = originalEnd;
        if (newEnd <= newStart) {
          newEnd = addMinutes(newStart, SNAP_MINUTES);
        }
      } else {
        newStart = snapTime(addMinutes(originalStart, deltaMinutes));
        newEnd = originalEnd;
        if (newStart >= newEnd) {
          newStart = addMinutes(newEnd, -SNAP_MINUTES);
        }
      }
    } else if (currentDragType === 'resize-bottom') {
      newStart = originalStart;
      const targetDate = typeof newTargetDate === 'object' ? newTargetDate?.date : newTargetDate;
      const targetEndHour = typeof newTargetDate === 'object' ? newTargetDate?.endHour : endHour;

      if (targetDate) {
        const [year, month, day] = targetDate.split('-').map(Number);
        const hourPart = Math.floor(targetEndHour);
        const minutePart = Math.round((targetEndHour - hourPart) * 60);
        newEnd = new Date(year, month - 1, day, hourPart, minutePart, 0);
        if (newEnd <= newStart) {
          newEnd = addMinutes(newStart, SNAP_MINUTES);
        }
      } else {
        newEnd = snapTime(addMinutes(originalEnd, deltaMinutes));
        if (newEnd <= newStart) {
          newEnd = addMinutes(newStart, SNAP_MINUTES);
        }
      }
    }

    const newStartHour = newStart.getHours() + newStart.getMinutes() / 60;
    const newEndHour = newEnd.getHours() + newEnd.getMinutes() / 60;
    const isCrossDay = newStart.toDateString() !== newEnd.toDateString();
    if (!isCrossDay && (newStartHour < startHour || newEndHour > endHour)) {
      return null;
    }
    if (isCrossDay && newStartHour < startHour) {
      return null;
    }

    return {
      newStart,
      newEnd,
      startTimeStr: format(newStart, 'yyyy-MM-ddTHH:mm:ss'),
      endTimeStr: format(newEnd, 'yyyy-MM-ddTHH:mm:ss'),
    };
  }, [startHour, endHour]);

  // Show projected times on the floating label during drag/resize
  const updateTimeLabel = useCallback((offset, currentDragType, currentEventDragState, currentTargetDay) => {
    const label = timeLabelRef.current;
    const el = eventRef.current;
    if (!label || !el || !currentEventDragState) {
      if (label) label.style.display = 'none';
      return;
    }

    const delta = currentDragType === 'resize-bottom' ? offset.height : offset.top;
    let targetDateInfo = null;
    if (currentTargetDay?.isResizeTop && currentTargetDay?.startHour !== undefined) {
      targetDateInfo = { date: currentTargetDay.date, startHour: currentTargetDay.startHour, isResizeTop: true };
    } else if (currentTargetDay?.endHour !== undefined) {
      targetDateInfo = { date: currentTargetDay.date, endHour: currentTargetDay.endHour };
    } else if (currentTargetDay?.date) {
      targetDateInfo = currentTargetDay.date;
    }

    const newTimes = calculateNewTimes(delta, currentDragType, currentEventDragState, targetDateInfo);
    if (!newTimes) {
      label.style.display = 'none';
      return;
    }

    const timeStr = `${formatHourToTime(newTimes.newStart)} – ${formatHourToTime(newTimes.newEnd)}`;
    label.textContent = timeStr;

    // Position the label above the event element
    const rect = el.getBoundingClientRect();
    label.style.display = 'block';
    label.style.top = `${rect.top - 26}px`;
    label.style.left = `${rect.left + (rect.width / 2)}px`;
  }, [calculateNewTimes]);

  // Hide floating time label
  const hideTimeLabel = useCallback(() => {
    const label = timeLabelRef.current;
    if (label) label.style.display = 'none';
  }, []);

  // Handle mouse down on resize handles
  const handleResizeStart = (e, type) => {
    // Only the primary button resizes. A right press must bubble to the card's
    // onContextMenu instead of starting a resize (no stopPropagation before this check).
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();

    wasDraggingRef.current = true;

    // Find current column for cross-day resize (use both X and Y for multi-row views)
    const currentColumn = findTargetColumn(e.clientX, e.clientY);

    dragTravelledRef.current = false;
    startEventDrag({
      type, // 'resize-top' or 'resize-bottom'
      eventType,
      event,
      originalEvent: { ...event },
      startX: e.clientX,
      startY: e.clientY,
      originalTop: top,
      originalHeight: height,
      originalColumn: currentColumn,
    });
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (dragDelayTimerRef.current) {
        clearTimeout(dragDelayTimerRef.current);
      }
    };
  }, []);

  // Actually start the drag operation
  const commitDrag = useCallback((pendingData, travelled = false) => {
    wasDraggingRef.current = true;
    dragTravelledRef.current = travelled;

    // Find the current column info (use both X and Y for multi-row views)
    const currentColumn = findTargetColumn(pendingData.startX, pendingData.startY);

    startEventDrag({
      type: 'move',
      eventType,
      event,
      originalEvent: { ...event },
      startX: pendingData.startX,
      startY: pendingData.startY,
      originalTop: top,
      originalHeight: height,
      originalColumn: currentColumn,
    });
  }, [startEventDrag, eventType, event, top, height, findTargetColumn]);

  // Handle mouse down for move (on main body)
  const handleMoveStart = (e) => {
    // Only the primary button drags. Without this a right press armed pendingDragRef and
    // the delay timer, which could start a drag or swallow the next left click.
    if (e.button !== 0) return;
    // Don't start drag if clicking on resize handles or buttons
    if (e.target.dataset.resizeHandle) return;
    if (e.target.closest('button')) return;

    // Don't allow moving multi-day events (too complex - use resize or edit dialog instead)
    if (event._isMultiDay) return;

    e.stopPropagation();

    // Store pending drag data
    pendingDragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
    };

    // Start delay timer - if held long enough, start drag
    dragDelayTimerRef.current = setTimeout(() => {
      if (pendingDragRef.current) {
        commitDrag(pendingDragRef.current);
        pendingDragRef.current = null;
      }
    }, DRAG_DELAY_MS);

    // Add temporary listeners to detect movement or release
    const handleEarlyMove = (moveEvent) => {
      if (!pendingDragRef.current) return;

      const dx = Math.abs(moveEvent.clientX - pendingDragRef.current.startX);
      const dy = Math.abs(moveEvent.clientY - pendingDragRef.current.startY);

      // If moved beyond threshold, start drag immediately
      if (dx > DRAG_MOVE_THRESHOLD || dy > DRAG_MOVE_THRESHOLD) {
        clearTimeout(dragDelayTimerRef.current);
        commitDrag(pendingDragRef.current, true);
        pendingDragRef.current = null;
        document.removeEventListener('mousemove', handleEarlyMove);
        document.removeEventListener('mouseup', handleEarlyRelease);
      }
    };

    const handleEarlyRelease = () => {
      // Released before drag started - this is a click
      clearTimeout(dragDelayTimerRef.current);
      pendingDragRef.current = null;
      document.removeEventListener('mousemove', handleEarlyMove);
      document.removeEventListener('mouseup', handleEarlyRelease);
      // Click will be handled by onClick handler
    };

    document.addEventListener('mousemove', handleEarlyMove);
    document.addEventListener('mouseup', handleEarlyRelease);
  };

  // (calculateNewTimes moved before updateTimeLabel to avoid TDZ reference error)

  // Actually persist the changes to API
  const commitChanges = useCallback(async (newTimes, sourceResourceId, targetResourceId, notify = true) => {
    if (!newTimes) return false;

    const { newStart, newEnd, startTimeStr, endTimeStr } = newTimes;

    try {
      if (eventType === 'maintenance') {
        const response = await axios.patch(`/api/scheduling/schedules/${event.id}/`, {
          start_time: startTimeStr,
          end_time: endTimeStr,
          ...(event.has_active_booking ? { notify_customer: notify } : {}),
        });

        // Tell the user what happened to the linked booking (synced always; message optional)
        if (response.data?.booking_synced) {
          setBookingSynced(response.data.booking_notified ? 'notified' : 'silent');
        }

        // Update local state with local timezone fields for multi-day rendering
        const localStartHour = newStart.getHours() + newStart.getMinutes() / 60;
        const localEndHour = newEnd.getHours() + newEnd.getMinutes() / 60;

        const timeUpdates = {
          start_time: startTimeStr,
          end_time: endTimeStr,
          local_start_date: format(newStart, 'yyyy-MM-dd'),
          local_end_date: format(newEnd, 'yyyy-MM-dd'),
          local_start_hour: localStartHour,
          local_end_hour: localEndHour,
        };

        // Handle employee reassignment (employee view only)
        const hasResourceSwap = currentView === 'employee' &&
          sourceResourceId && targetResourceId &&
          sourceResourceId !== targetResourceId;

        if (hasResourceSwap) {
          // With `schedule` only this slot changes rows; other slots of the same work
          // order keep their mechanics. The response's employee_ids is the slot team.
          const swapResponse = await axios.post('/api/scheduling/maintenance-employees/swap/', {
            maintenance: event.maintenance_id,
            schedule: event.id,
            old_employee: sourceResourceId,
            new_employee: targetResourceId,
          });
          updateEvent(event.id, {
            ...timeUpdates,
            employee_ids: swapResponse.data.employee_ids,
          });
        } else {
          updateEvent(event.id, timeUpdates);
        }
      } else if (eventType === 'unavailability') {
        await axios.patch(`/api/scheduling/unavailabilities/${event.id}/`, {
          start_time: startTimeStr,
          end_time: endTimeStr,
        });

        const localStartHour = newStart.getHours() + newStart.getMinutes() / 60;
        const localEndHour = newEnd.getHours() + newEnd.getMinutes() / 60;

        updateUnavailability(event.id, {
          start_time: startTimeStr,
          end_time: endTimeStr,
          local_start_hour: localStartHour,
          local_end_hour: localEndHour,
          local_start_date: format(newStart, 'yyyy-MM-dd'),
          local_end_date: format(newEnd, 'yyyy-MM-dd'),
        });
      } else if (eventType === 'calendar_note') {
        // Dropping a note in another mechanic's column moves the note to that column.
        const movedToAnotherColumn = currentView === 'employee' &&
          sourceResourceId && targetResourceId &&
          sourceResourceId !== targetResourceId;

        const payload = {
          start_time: startTimeStr,
          end_time: endTimeStr,
        };
        if (movedToAnotherColumn) {
          payload.employee = targetResourceId;
        }
        await axios.patch(`/api/scheduling/calendar-notes/${event.id}/`, payload);

        const localStartHour = newStart.getHours() + newStart.getMinutes() / 60;
        const localEndHour = newEnd.getHours() + newEnd.getMinutes() / 60;

        updateCalendarNote(event.id, {
          start_time: startTimeStr,
          end_time: endTimeStr,
          local_start_hour: localStartHour,
          local_end_hour: localEndHour,
          local_start_date: format(newStart, 'yyyy-MM-dd'),
          local_end_date: format(newEnd, 'yyyy-MM-dd'),
          ...(movedToAnotherColumn
            ? {
                employee_id: targetResourceId,
                employee_name: employees.find((e) => e.id === targetResourceId)?.name || '',
              }
            : {}),
        });
      }
      return true;
    } catch (err) {
      console.error('Failed to update event:', err);
      const message = err.response?.data?.detail || err.response?.data?.message || t('Failed to update event');
      setUpdateError(message);
      return false;
    }
  }, [eventType, event.id, event.maintenance_id, event.has_active_booking, currentView, employees, updateEvent, updateUnavailability, updateCalendarNote, t]);

  // Helper to reset DOM styles (used by confirm/cancel handlers)
  const resetDOMStyles = useCallback(() => {
    const el = eventRef.current;
    if (el) {
      el.style.top = `${top}px`;
      el.style.height = `${height}px`;
      el.style.transform = 'none';
    }
    localDragOffsetRef.current = { top: 0, height: 0, left: 0 };
    hideCrossDayOverlay();
  }, [top, height, hideCrossDayOverlay]);

  // Handle confirmation dialog
  const handleConfirm = async () => {
    await commitChanges(
      confirmDialog.newTimes,
      confirmDialog.sourceResourceId,
      confirmDialog.targetResourceId,
      notifyCustomer
    );
    // Reset DOM styles - calendar will re-render with new data in correct position
    resetDOMStyles();
    targetDayInfoRef.current = null;
    setConfirmDialog({ open: false, pendingDelta: 0, newTimes: null, sourceResourceId: null, targetResourceId: null });
    setNotifyCustomer(true);
  };

  const handleCancel = () => {
    resetDOMStyles();
    targetDayInfoRef.current = null;
    setConfirmDialog({ open: false, pendingDelta: 0, newTimes: null, sourceResourceId: null, targetResourceId: null });
    setNotifyCustomer(true);
  };

  // Direct DOM update for smooth drag/resize (no React re-renders during drag)
  const updateElementStyle = useCallback((offset) => {
    const el = eventRef.current;
    if (!el) return;

    const newTop = top + offset.top;
    const newHeight = height + offset.height;

    el.style.top = `${newTop}px`;
    el.style.height = `${newHeight}px`;
    if (offset.left) {
      el.style.transform = `translateX(${offset.left}px)`;
    } else {
      el.style.transform = 'none';
    }

    // Store in ref for mouseUp handler
    localDragOffsetRef.current = offset;
  }, [top, height]);

  // Set inline styles via useLayoutEffect - runs AFTER React DOM updates but BEFORE paint
  // This ensures our styles are always applied correctly regardless of React's style prop handling
  // For non-dragged elements: always set to prop values
  // For dragged element: don't touch (drag handlers manage it)
  // For dragged element with open confirm dialog: keep current position (waiting for confirm/cancel)
  useLayoutEffect(() => {
    const el = eventRef.current;
    if (!el) return;

    // Skip if this element is being dragged OR if confirm dialog is open (keep drag position)
    if (isBeingDragged || confirmDialog.open) {
      return;
    }

    // For all non-dragged elements, set their correct positions
    el.style.top = `${top}px`;
    el.style.height = `${height}px`;
    el.style.transform = 'none';
  }, [top, height, isBeingDragged, confirmDialog.open]);

  // Handle drag movement and completion
  useEffect(() => {
    if (!isBeingDragged) {
      return;
    }

    const handleMouseMove = (e) => {
      if (
        Math.abs(e.clientX - eventDragState.startX) > DRAG_MOVE_THRESHOLD ||
        Math.abs(e.clientY - eventDragState.startY) > DRAG_MOVE_THRESHOLD
      ) {
        dragTravelledRef.current = true;
      }
      const rawDeltaY = e.clientY - eventDragState.startY;
      const snappedDeltaY = snapToGrid(rawDeltaY);

      if (dragType === 'move') {
        // Track horizontal position for cross-day dragging (use Y to stay in same row)
        const targetColumn = findTargetColumn(e.clientX, e.clientY);
        let leftOffset = 0;

        if (targetColumn && eventDragState.originalColumn) {
          // Calculate horizontal offset based on column positions
          const originalColumnCenter = eventDragState.originalColumn.columnLeft + eventDragState.originalColumn.columnWidth / 2;
          const targetColumnCenter = targetColumn.columnLeft + targetColumn.columnWidth / 2;
          leftOffset = targetColumnCenter - originalColumnCenter;

          // Update target day info ref (NO STATE UPDATE)
          const dateChanged = targetColumn.date !== eventDragState.originalColumn.date;
          const resourceChanged = targetColumn.resourceId !== eventDragState.originalColumn.resourceId;
          if (dateChanged || resourceChanged) {
            targetDayInfoRef.current = targetColumn;
          } else {
            targetDayInfoRef.current = null;
          }
        }

        // Clamp to valid range visually
        const newTop = eventDragState.originalTop + snappedDeltaY;
        const eventBottom = newTop + eventDragState.originalHeight;

        // Only allow if within work day bounds
        if (newTop >= 0 && eventBottom <= workDayPixels) {
          const moveOffset = { top: snappedDeltaY, height: 0, left: leftOffset };
          requestAnimationFrame(() => {
            updateElementStyle(moveOffset);
            updateTimeLabel(moveOffset, dragType, eventDragState, targetDayInfoRef.current);
          });
        }
      } else if (dragType === 'resize-top') {
        // Check for cross-day resize-top (extending start to earlier day)
        const targetColumn = findTargetColumn(e.clientX, eventDragState.originalColumn?.columnTop);
        if (targetColumn && eventDragState.originalColumn) {
          const originalDayIndex = eventDragState.originalColumn.dayIndex;
          const targetDayIndex = targetColumn.dayIndex;

          // Extending start to an earlier day
          if (targetDayIndex < originalDayIndex) {
            // Calculate the target hour from mouse Y position in the target column
            const relativeY = e.clientY - targetColumn.columnTop;
            const rawHour = (relativeY / HOUR_HEIGHT) + startHour;
            const snappedHour = Math.round(rawHour * SNAPS_PER_HOUR) / SNAPS_PER_HOUR;
            const clampedHour = Math.max(startHour, Math.min(endHour, snappedHour));

            // Update target day ref with startHour for resize-top
            targetDayInfoRef.current = { ...targetColumn, startHour: clampedHour, isResizeTop: true };

            // Show cross-day overlay via direct DOM manipulation
            requestAnimationFrame(() => {
              updateCrossDayOverlay(targetColumn, clampedHour, 'extend-top');
              // Extend current day to start at beginning of work day
              const extendToStartOfDay = eventDragState.originalTop;
              const extOffset = { top: -extendToStartOfDay, height: extendToStartOfDay, left: 0 };
              updateElementStyle(extOffset);
              updateTimeLabel(extOffset, dragType, eventDragState, targetDayInfoRef.current);
            });
            return;
          }

          // Shrinking start to a later day (for multi-day events)
          if (targetDayIndex > originalDayIndex) {
            // Get the end date of the original event to prevent shrinking past it
            const originalEndDate = eventDragState.originalEvent.end_time?.split('T')[0];
            const targetDate = targetColumn.date;

            // Don't allow shrinking past or to the end date
            if (targetDate >= originalEndDate) {
              hideCrossDayOverlay();
              targetDayInfoRef.current = null;
              return;
            }

            const relativeY = e.clientY - targetColumn.columnTop;
            const rawHour = (relativeY / HOUR_HEIGHT) + startHour;
            const snappedHour = Math.round(rawHour * SNAPS_PER_HOUR) / SNAPS_PER_HOUR;
            const clampedHour = Math.max(startHour, Math.min(endHour, snappedHour));

            // Update target day ref with startHour and shrinking flag
            targetDayInfoRef.current = { ...targetColumn, startHour: clampedHour, isResizeTop: true, isShrinking: true };

            requestAnimationFrame(() => {
              // Show overlay from start of day to the new start hour on target day
              updateCrossDayOverlay(targetColumn, clampedHour, 'shrink-top');
              // Shrink current day's portion to nothing (will be removed)
              const shrinkOffset = { top: eventDragState.originalHeight, height: -eventDragState.originalHeight, left: 0 };
              updateElementStyle(shrinkOffset);
              updateTimeLabel(shrinkOffset, dragType, eventDragState, targetDayInfoRef.current);
            });
            return;
          }
        }

        // Hide overlay if not in cross-day mode
        hideCrossDayOverlay();
        targetDayInfoRef.current = null;

        // Normal single-day resize from top: ensure minimum height and within bounds
        const minHeight = SNAP_PIXELS;
        const maxDelta = eventDragState.originalHeight - minHeight;
        const clampedDelta = Math.min(snappedDeltaY, maxDelta);

        // Ensure we don't go above the top of the grid
        const newTop = eventDragState.originalTop + clampedDelta;
        if (newTop >= 0) {
          const resTopOffset = { top: clampedDelta, height: -clampedDelta, left: 0 };
          requestAnimationFrame(() => {
            updateElementStyle(resTopOffset);
            updateTimeLabel(resTopOffset, dragType, eventDragState, null);
          });
        }
      } else if (dragType === 'resize-bottom') {
        // Check for cross-day resize (for both maintenance and unavailability events)
        // Use original column's Y to stay in the same employee/resource row
        const targetColumn = findTargetColumn(e.clientX, eventDragState.originalColumn?.columnTop);
        if (targetColumn && eventDragState.originalColumn) {
          const originalDayIndex = eventDragState.originalColumn.dayIndex;
          const targetDayIndex = targetColumn.dayIndex;

          // Extending to a later day
          if (targetDayIndex > originalDayIndex) {
            // Calculate the target hour from mouse Y position in the target column
            const relativeY = e.clientY - targetColumn.columnTop;
            const rawHour = (relativeY / HOUR_HEIGHT) + startHour;
            const snappedHour = Math.round(rawHour * SNAPS_PER_HOUR) / SNAPS_PER_HOUR;
            const clampedHour = Math.max(startHour, Math.min(endHour, snappedHour));

            // Update target day ref (NO STATE UPDATE - use overlay instead)
            targetDayInfoRef.current = { ...targetColumn, endHour: clampedHour };

            // Show cross-day overlay via direct DOM manipulation
            requestAnimationFrame(() => {
              updateCrossDayOverlay(targetColumn, clampedHour, 'extend');
              // Extend current day to end of work day
              const extendToEndOfDay = workDayPixels - eventDragState.originalTop - eventDragState.originalHeight;
              const extBotOffset = { top: 0, height: extendToEndOfDay, left: 0 };
              updateElementStyle(extBotOffset);
              updateTimeLabel(extBotOffset, dragType, eventDragState, targetDayInfoRef.current);
            });
            return;
          }

          // Shrinking to an earlier day
          if (targetDayIndex < originalDayIndex) {
            // Get the start date of the original event to prevent shrinking past it
            const originalStartDate = eventDragState.originalEvent.start_time?.split('T')[0];
            const targetDate = targetColumn.date;

            // Don't allow shrinking past or to the start date
            if (targetDate <= originalStartDate) {
              hideCrossDayOverlay();
              targetDayInfoRef.current = null;
              return;
            }

            const relativeY = e.clientY - targetColumn.columnTop;
            const rawHour = (relativeY / HOUR_HEIGHT) + startHour;
            const snappedHour = Math.round(rawHour * SNAPS_PER_HOUR) / SNAPS_PER_HOUR;
            const clampedHour = Math.max(startHour, Math.min(endHour, snappedHour));

            // Update target day ref (NO STATE UPDATE)
            targetDayInfoRef.current = { ...targetColumn, endHour: clampedHour, isShrinking: true };

            requestAnimationFrame(() => {
              updateCrossDayOverlay(targetColumn, clampedHour, 'shrink');
              // Shrink current day's portion to nothing (will be removed)
              const shrinkBotOffset = { top: 0, height: -eventDragState.originalHeight, left: 0 };
              updateElementStyle(shrinkBotOffset);
              updateTimeLabel(shrinkBotOffset, dragType, eventDragState, targetDayInfoRef.current);
            });
            return;
          }
        }

        // Hide overlay if not in cross-day mode
        hideCrossDayOverlay();
        targetDayInfoRef.current = null;

        // Normal single-day resize
        const minHeight = SNAP_PIXELS;
        const maxShrink = -(eventDragState.originalHeight - minHeight);
        const clampedDelta = Math.max(maxShrink, snappedDeltaY);

        const newBottom = eventDragState.originalTop + eventDragState.originalHeight + clampedDelta;
        if (newBottom <= workDayPixels) {
          const resBotOffset = { top: 0, height: clampedDelta, left: 0 };
          requestAnimationFrame(() => {
            updateElementStyle(resBotOffset);
            updateTimeLabel(resBotOffset, dragType, eventDragState, null);
          });
        }
      }
    };

    // Helper to reset DOM styles
    const resetElementStyle = () => {
      const el = eventRef.current;
      if (el) {
        el.style.top = `${top}px`;
        el.style.height = `${height}px`;
        el.style.transform = 'none';
      }
      localDragOffsetRef.current = { top: 0, height: 0, left: 0 };
      hideCrossDayOverlay();
      hideTimeLabel();
    };

    const handleMouseUp = () => {
      hideTimeLabel();
      // Read from refs to avoid stale closures and dependency issues
      const currentOffset = localDragOffsetRef.current;
      const currentTargetDay = targetDayInfoRef.current;

      const snappedDelta = currentOffset.top || currentOffset.height;
      const hasDayChange = currentTargetDay !== null;
      const hasMovement = Math.abs(snappedDelta) >= SNAP_PIXELS || hasDayChange;

      // Only show confirmation if there was meaningful movement (time or day change)
      if (hasMovement) {
        const delta = dragType === 'resize-bottom' ? currentOffset.height : currentOffset.top;
        // For cross-day resize, pass the full targetDayInfo including endHour or startHour
        let targetDateInfo = null;
        if (currentTargetDay?.isResizeTop && currentTargetDay?.startHour !== undefined) {
          // Resize-top cross-day: pass startHour and isResizeTop flag
          targetDateInfo = { date: currentTargetDay.date, startHour: currentTargetDay.startHour, isResizeTop: true };
        } else if (currentTargetDay?.endHour !== undefined) {
          // Resize-bottom cross-day: pass endHour
          targetDateInfo = { date: currentTargetDay.date, endHour: currentTargetDay.endHour };
        } else if (currentTargetDay?.date) {
          // Move cross-day: just pass date
          targetDateInfo = currentTargetDay.date;
        }
        const newTimes = calculateNewTimes(delta, dragType, eventDragState, targetDateInfo);

        if (newTimes) {
          // Show confirmation dialog with resource info for employee swap
          setConfirmDialog({
            open: true,
            pendingDelta: delta,
            newTimes,
            sourceResourceId: eventDragState?.originalColumn?.resourceId || null,
            targetResourceId: currentTargetDay?.resourceId || null,
          });
        } else {
          // Invalid position - reset DOM directly
          resetElementStyle();
          targetDayInfoRef.current = null;
        }
      } else {
        // No significant movement - reset DOM directly
        resetElementStyle();
        targetDayInfoRef.current = null;
      }

      endEventDrag();

      if (!hasMovement && !dragTravelledRef.current) {
        // The pointer never left the spot, so this was a click: let it open the card. A resize
        // starts on mousedown, and on a short card the handles are most of the card.
        // A drag that went away and came back is still a cancelled drag, not a click.
        wasDraggingRef.current = false;
        return;
      }
      // Clear drag flag after a short delay to prevent click
      setTimeout(() => {
        wasDraggingRef.current = false;
      }, 100);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isBeingDragged, dragType, eventDragState, endEventDrag, calculateNewTimes, workDayPixels, findTargetColumn, updateElementStyle, updateTimeLabel, hideTimeLabel, startHour, endHour, top, height, updateCrossDayOverlay, hideCrossDayOverlay]);

  const handleClick = (e) => {
    // Don't trigger click if we were dragging or just finished dragging. The drag state is read
    // from the store, not this render: the mouseup that ended the drag cleared it just now.
    if (wasDraggingRef.current || useCalendarState.getState().eventDragState || pendingDragRef.current) {
      e.stopPropagation();
      e.preventDefault();
      return;
    }
    onClick?.(e);
  };

  // Right-click menu on maintenance cards only ("Open work order" needs no extra permission;
  // the menu hides the actions the user lacks). Other card types keep the browser menu.
  const hasEventMenu = eventType === 'maintenance';

  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Never on top of, or right after, a drag (wasDraggingRef stays set 100 ms after drop)
    if (eventDragState || wasDraggingRef.current) return;
    // macOS ctrl+click fires mousedown(button 0) before contextmenu: cancel that pending drag
    if (dragDelayTimerRef.current) clearTimeout(dragDelayTimerRef.current);
    pendingDragRef.current = null;
    setTooltipOpen(false);
    openEventContextMenu(event, { x: e.clientX, y: e.clientY });
  };

  // Format time for display in dialog (HH:mm with colon separator)
  const formatTimeDisplay = (date) => {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const formatDateDisplay = (date) => {
    return date.toLocaleDateString('fi-FI', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  return (
    <>
      {/* Cross-day resize overlay - fixed position, direct DOM manipulation */}
      <div ref={crossDayOverlayRef} style={{ display: 'none' }} />

      {/* Floating time label shown during drag/resize */}
      <div
        ref={timeLabelRef}
        style={{
          display: 'none',
          position: 'fixed',
          transform: 'translateX(-50%)',
          backgroundColor: 'rgba(30, 41, 59, 0.92)',
          color: '#fff',
          fontSize: '12px',
          fontWeight: 600,
          padding: '2px 8px',
          borderRadius: '6px',
          zIndex: 1100,
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
          fontFamily: "'Plus Jakarta Sans', sans-serif",
        }}
      />

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={handleCancel}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        data-testid="event-move-confirm-dialog"
        PaperProps={{ sx: { borderRadius: '12px', minWidth: 320 } }}
      >
        <DialogTitle sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, pb: 1 }}>
          {t('Confirm schedule change')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, color: 'text.secondary', fontSize: '14px' }}>
            {t('Are you sure you want to change the schedule?')}
          </Typography>

          {/* Linked booking: the slot move syncs it; the checkbox decides about the message */}
          {event.has_active_booking && (
            <BookingNotifyChoice
              checked={notifyCustomer}
              onChange={setNotifyCustomer}
              // A pure resize keeps the start: the backend stays silent, so no choice to offer
              showChoice={
                !confirmDialog.newTimes ||
                new Date(event.start_time).getTime() !== confirmDialog.newTimes.newStart.getTime()
              }
              sx={{ mb: 2 }}
            />
          )}

          {/* Conflict Warning */}
          {dragConflicts.length > 0 && (
            <Alert
              severity="warning"
              icon={<WarningAmberIcon />}
              sx={{ mb: 2, borderRadius: '8px' }}
            >
              <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: '13px' }}>
                {t('Conflict detected')}
              </Typography>
              {dragConflicts.map((conflict, idx) => (
                <Typography key={idx} sx={{ fontSize: '12px' }}>
                  {conflict.message}
                </Typography>
              ))}
            </Alert>
          )}

          {confirmDialog.newTimes && (
            <Box sx={{ bgcolor: 'action.hover', borderRadius: '8px', p: 2 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 0.5 }}>
                {t('New time')}:
              </Typography>
              <Typography sx={{ fontSize: '14px' }}>
                {confirmDialog.newTimes.newStart.toDateString() !== confirmDialog.newTimes.newEnd.toDateString() ? (
                  // Multi-day event - show both dates
                  <>
                    {formatDateDisplay(confirmDialog.newTimes.newStart)}{' '}
                    {formatTimeDisplay(confirmDialog.newTimes.newStart)}
                    {' → '}
                    {formatDateDisplay(confirmDialog.newTimes.newEnd)}{' '}
                    {formatTimeDisplay(confirmDialog.newTimes.newEnd)}
                  </>
                ) : (
                  // Same-day event
                  <>
                    {formatDateDisplay(confirmDialog.newTimes.newStart)}{' '}
                    {formatTimeDisplay(confirmDialog.newTimes.newStart)} - {formatTimeDisplay(confirmDialog.newTimes.newEnd)}
                  </>
                )}
              </Typography>
            </Box>
          )}

          {/* Employee change info (employee view only) */}
          {currentView === 'employee' &&
            confirmDialog.sourceResourceId && confirmDialog.targetResourceId &&
            confirmDialog.sourceResourceId !== confirmDialog.targetResourceId && (
            <Box sx={{ bgcolor: 'action.hover', borderRadius: '8px', p: 2, mt: 1 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 0.5 }}>
                {t('Employee change')}:
              </Typography>
              <Typography sx={{ fontSize: '14px' }}>
                {employees.find(e => e.id === confirmDialog.sourceResourceId)?.name || confirmDialog.sourceResourceId}
                {' → '}
                {employees.find(e => e.id === confirmDialog.targetResourceId)?.name || confirmDialog.targetResourceId}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancel} sx={{ borderRadius: '8px' }}>
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            sx={{ borderRadius: '8px', bgcolor: 'secondary.main', '&:hover': { bgcolor: 'secondary.dark' } }}
          >
            {t('Confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Tooltip
        title={tooltipContent || ''}
        // Controlled so the 750 ms enter timer cannot open it above a context menu
        // (tooltip Popper z-index 1500 sits above the menu at 1000).
        open={tooltipOpen && !isAnyEventMenuOpen}
        onOpen={() => {
          if (!useCalendarState.getState().eventContextMenu) setTooltipOpen(true);
        }}
        onClose={() => setTooltipOpen(false)}
        enterDelay={750}
        enterNextDelay={350}
        disableHoverListener={isBeingDragged || isAnyDragActive || !tooltipContent}
        placement="right"
        arrow
        slotProps={{
          tooltip: {
            sx: {
              bgcolor: 'background.paper',
              color: 'text.primary',
              boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
              borderRadius: '10px',
              p: 0,
              maxWidth: 320,
            },
          },
          arrow: { sx: { color: 'background.paper' } },
        }}
      >
      <Box
        ref={eventRef}
        data-event="true"
        data-testid={eventType === 'maintenance' ? `calendar-event-${event.id}` : undefined}
        data-state={eventType === 'maintenance' ? event.state : undefined}
        data-color-tag={colorTagId != null ? String(colorTagId) : undefined}
        data-color-tag-mode={colorTagMode || undefined}
        data-highlighted={highlighted ? 'true' : undefined}
        data-event-id={event.id}
        data-event-type={eventType}
        data-day-index={dayIndex}
        data-overlap-index={overlapIndex}
        data-overlap-total={overlapTotal}
        onClick={handleClick}
        onMouseDown={canSchedule ? handleMoveStart : undefined}
        onContextMenu={hasEventMenu ? handleContextMenu : undefined}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        // NOTE: top/height are managed via useLayoutEffect, NOT via style or sx prop.
        // This prevents React from overwriting our direct DOM manipulation during drag.
        sx={{
          position: 'absolute',
          // Use percentage-based positioning for overlapping events
          left: overlapTotal > 1 ? `calc(${(overlapIndex / overlapTotal) * 100}% + 2px)` : 4,
          width: overlapTotal > 1 ? `calc(${100 / overlapTotal}% - 4px)` : undefined,
          right: overlapTotal > 1 ? undefined : 4,
          // top and height are in style prop, not here!
          bgcolor: backgroundColor,
          // A ring tag keeps the state bar readable by widening it, and is drawn with
          // `outline` rather than `boxShadow` (taken by the drag and hover elevation) or
          // `border` (would resize the content box mid-drag).
          borderLeft: `${ringColor ? 5 : 3}px solid ${borderColor}`,
          ...(ringColor ? { outline: `2px solid ${ringColor}`, outlineOffset: '-2px' } : null),
          borderRadius: '8px',
          cursor: isBeingDragged ? 'grabbing' : (event._isMultiDay ? 'pointer' : 'grab'),
          overflow: 'hidden',
          zIndex: isBeingDragged || confirmDialog.open ? 100 : 2,
          opacity: isBeingDragged ? 0.9 : 1,
          boxShadow: isBeingDragged ? '0 4px 20px rgba(0,0,0,0.25)' : 'none',
          // Disable transitions during ANY drag to prevent visual glitches on siblings
          transition: isAnyDragActive ? 'none' : 'top 0.1s ease, height 0.1s ease, box-shadow 0.1s ease, transform 0.1s ease',
          userSelect: 'none',
          '&:hover': {
            boxShadow: isBeingDragged ? '0 4px 20px rgba(0,0,0,0.25)' : '0 2px 8px rgba(0,0,0,0.1)',
          },
          ...sx,
        }}
      >
      {/* Top resize handle - only show for single-day events OR first day of multi-day */}
      {(!event._isMultiDay || event._dayPosition === 'first') && (
        <Box
          data-resize-handle="top"
          onMouseDown={canSchedule ? (e) => handleResizeStart(e, 'resize-top') : undefined}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: resizeHandleHeight,
            cursor: 'ns-resize',
            bgcolor: isHovered ? 'rgba(0,0,0,0.1)' : 'transparent',
            borderRadius: '8px 8px 0 0',
            transition: 'background-color 0.15s',
            zIndex: 10,
          }}
        />
      )}

      {/* Content */}
      <Box sx={{ p: '6px 8px', height: '100%', pointerEvents: 'none' }}>
        {children}
      </Box>

      {/* Bottom resize handle - only show for single-day events OR last day of multi-day */}
      {(!event._isMultiDay || event._dayPosition === 'last') && (
        <Box
          data-resize-handle="bottom"
          onMouseDown={canSchedule ? (e) => handleResizeStart(e, 'resize-bottom') : undefined}
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: resizeHandleHeight,
            cursor: 'ns-resize',
            bgcolor: isHovered ? 'rgba(0,0,0,0.1)' : 'transparent',
            borderRadius: '0 0 8px 8px',
            transition: 'background-color 0.15s',
            zIndex: 10,
          }}
        />
      )}
      </Box>
      </Tooltip>

      {/* Error Snackbar for update failures */}
      <Snackbar
        open={!!updateError}
        autoHideDuration={5000}
        onClose={() => setUpdateError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setUpdateError(null)} severity="error" sx={{ width: '100%' }}>
          {updateError}
        </Alert>
      </Snackbar>

      {/* Booking synced Snackbar */}
      <Snackbar
        open={!!bookingSynced}
        autoHideDuration={4000}
        onClose={() => setBookingSynced(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setBookingSynced(null)}
          severity="info"
          sx={{ width: '100%' }}
          data-testid="booking-synced-snackbar"
        >
          {bookingSynced === 'notified'
            ? t('booking_linked_updated_notified')
            : t('booking_linked_updated_silent')}
        </Alert>
      </Snackbar>
    </>
  );
}

export default DraggableEvent;
